import { createContext, useContext, useState, useEffect } from "react";
import { NexApi } from "src/lib/neuroexchangeApi";
import i18n from "src/i18n/index";

const ProjectContext = createContext();
const PROJECTS_POLL_INTERVAL_MS = 30000;

export function ProjectProvider({ children }) {
  const [project, setProject] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [localization, setLocalization] = useState(
    i18n?.resolvedLanguage || i18n?.language || "pt-BR"
  );

  useEffect(() => {
    const onLanguageChanged = (language) => {
      const next = language || i18n?.resolvedLanguage || i18n?.language || "pt-BR";
      setLocalization((prev) => (prev === next ? prev : next));
    };

    i18n.on("languageChanged", onLanguageChanged);
    return () => {
      i18n.off("languageChanged", onLanguageChanged);
    };
  }, []);

  useEffect(() => {
    let active = true;
    let requestInFlight = false;

    const loadProjects = async (initialLoad = false) => {
      if (requestInFlight) return;
      requestInFlight = true;

      if (initialLoad) {
        setLoading(true);
        setError(null);
      }

      try {
        const data = await NexApi.getProjects(localization);
        if (active) setProjects(Array.isArray(data) ? data : []);
        if (active) setError(null);
      } catch (e) {
        if (active && initialLoad) {
          setError(e);
          setProjects([]);
        } else if (active) {
          console.warn('[projectContext] Failed to refresh projects', e);
        }
      } finally {
        requestInFlight = false;
        if (active && initialLoad) setLoading(false);
      }
    };

    loadProjects(true);
    const intervalId = window.setInterval(() => loadProjects(false), PROJECTS_POLL_INTERVAL_MS);

    return () => {
      active = false;
      window.clearInterval(intervalId);
    };
  }, [localization]);

  return (
    <ProjectContext.Provider
      value={{
        project,
        setProject,
        projects,
        loading,
        error,
      }}
    >
      {children}
    </ProjectContext.Provider>
  );
}

export function useProject() {
  return useContext(ProjectContext);
}
